import { useState, useEffect, useMemo } from 'react'
import { useApp } from '@/store/AppContext'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import {
  Search,
  Clock,
  ArrowRight,
  Car,
  Bike,
  Package,
  AlertCircle,
} from 'lucide-react'
import { differenceInMinutes } from 'date-fns'
import { VehicleType } from '@/lib/types'
import { calculatePrice, formatCurrency } from '@/lib/pricing'
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group'
import { CheckoutSheet } from './CheckoutSheet'

function formatDuration(minutes: number) {
  const h = Math.floor(minutes / 60)
  const m = minutes % 60
  return `${h}h ${m}m`
}

function getDurationColorClass(minutes: number) {
  if (minutes <= 60) return 'text-emerald-700 bg-emerald-50 border-emerald-200'
  if (minutes <= 180) return 'text-blue-700 bg-blue-50 border-blue-200'
  return 'text-indigo-800 bg-indigo-100 border-indigo-300'
}

export function ActiveTickets() {
  const { tickets, rules, spaces, isOperationMode } = useApp()
  const [search, setSearch] = useState('')
  const [now, setNow] = useState(new Date())
  const [filterType, setFilterType] = useState<VehicleType | 'Todos'>('Todos')
  const [filterNotes, setFilterNotes] = useState(false)
  const [checkoutTicketId, setCheckoutTicketId] = useState<string | null>(null)

  useEffect(() => {
    const timer = setInterval(() => setNow(new Date()), 60000)
    return () => clearInterval(timer)
  }, [])

  const active = useMemo(() => {
    let result = tickets.filter((t) => t.status === 'active')
    if (search)
      result = result.filter((t) => t.plate.includes(search.toUpperCase()))
    if (filterType !== 'Todos')
      result = result.filter((t) => t.type === filterType)
    if (filterNotes)
      result = result.filter((t) => t.notes && t.notes.trim().length > 0)

    return result.sort((a, b) => a.checkInAt.getTime() - b.checkInAt.getTime())
  }, [tickets, search, filterType, filterNotes])

  return (
    <>
      <Card className="flex flex-col h-full border-none shadow-sm md:border-solid md:shadow-elevation bg-muted/10 md:bg-card">
        <CardHeader className="pb-3 px-0 md:px-6">
          <div className="flex flex-col space-y-4">
            <div className="flex justify-between items-center">
              <CardTitle
                className={
                  isOperationMode
                    ? 'text-2xl flex items-center gap-2'
                    : 'text-xl flex items-center gap-2'
                }
              >
                Pátio
                <Badge
                  variant="secondary"
                  className="bg-primary/10 text-primary hover:bg-primary/20 text-sm"
                >
                  {active.length}
                </Badge>
              </CardTitle>
              <div className="relative w-full sm:w-64">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Buscar placa..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-9 h-10 bg-background uppercase"
                />
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-2">
              <ToggleGroup
                type="single"
                value={filterType}
                onValueChange={(v) => v && setFilterType(v as any)}
                className="justify-start"
              >
                <ToggleGroupItem
                  value="Todos"
                  className="h-8 px-3 text-xs border"
                >
                  Todos
                </ToggleGroupItem>
                <ToggleGroupItem
                  value="Carro"
                  className="h-8 px-3 text-xs border"
                >
                  <Car className="h-3 w-3 mr-1" /> Carros
                </ToggleGroupItem>
                <ToggleGroupItem
                  value="Moto"
                  className="h-8 px-3 text-xs border"
                >
                  <Bike className="h-3 w-3 mr-1" /> Motos
                </ToggleGroupItem>
              </ToggleGroup>
              <Button
                variant={filterNotes ? 'default' : 'outline'}
                size="sm"
                className="h-8 text-xs"
                onClick={() => setFilterNotes(!filterNotes)}
              >
                <AlertCircle className="h-3 w-3 mr-1" /> C/ Obs
              </Button>
            </div>
          </div>
        </CardHeader>

        <CardContent className="flex-1 overflow-auto p-0 md:p-6 md:pt-0 pb-20">
          <div className="space-y-3">
            {active.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground bg-background rounded-lg border border-dashed flex flex-col items-center justify-center">
                <Car className="h-12 w-12 text-muted/50 mb-3" />
                <p className="font-medium text-lg text-foreground">
                  Nenhum veículo no pátio agora
                </p>
                <p className="text-sm mb-4">
                  Use o formulário para registrar uma entrada.
                </p>
                <Button
                  variant="outline"
                  onClick={() => document.getElementById('plate')?.focus()}
                >
                  Registrar Entrada ( / )
                </Button>
              </div>
            ) : (
              active.map((ticket) => {
                const mins = differenceInMinutes(now, ticket.checkInAt)
                const price = calculatePrice(ticket, rules)
                const TypeIcon =
                  ticket.type === 'Carro'
                    ? Car
                    : ticket.type === 'Moto'
                      ? Bike
                      : Package
                const spaceNumber = ticket.spaceId
                  ? spaces.find((s) => s.id === ticket.spaceId)?.number
                  : null

                return (
                  <div
                    key={ticket.id}
                    className="group flex flex-col sm:flex-row sm:items-center justify-between p-4 rounded-xl border bg-card shadow-sm hover:border-primary/50 hover:shadow-md transition-all gap-4"
                  >
                    <div className="flex flex-col gap-2">
                      <div className="flex items-center gap-2">
                        <span
                          className={`text-2xl font-black tracking-widest ${isOperationMode ? 'text-3xl' : ''}`}
                        >
                          {ticket.plate}
                        </span>
                        {spaceNumber && (
                          <Badge
                            variant="outline"
                            className="bg-primary/5 text-primary border-primary/20 text-xs px-2 h-6"
                          >
                            Vaga {spaceNumber}
                          </Badge>
                        )}
                        {ticket.notes && (
                          <div
                            className="bg-amber-100 text-amber-800 p-1 rounded-full animate-pulse-ring"
                            title={ticket.notes}
                          >
                            <AlertCircle className="h-4 w-4" />
                          </div>
                        )}
                      </div>

                      <div className="flex flex-wrap items-center gap-2 text-sm">
                        <Badge
                          variant="outline"
                          className="text-xs font-normal border-muted-foreground/20"
                        >
                          <TypeIcon className="h-3 w-3 mr-1" /> {ticket.type}
                        </Badge>
                        <span className="text-muted-foreground flex items-center">
                          <Clock className="h-3 w-3 mr-1" />
                          {ticket.checkInAt.toLocaleTimeString('pt-BR', {
                            hour: '2-digit',
                            minute: '2-digit',
                          })}
                        </span>
                        <div
                          className={`px-2 py-0.5 rounded-full text-xs font-bold border ${getDurationColorClass(mins)}`}
                        >
                          {formatDuration(mins)}
                        </div>
                        {ticket.operatorName && (
                          <span className="text-xs text-muted-foreground border-l pl-2">
                            {ticket.operatorName}
                          </span>
                        )}
                      </div>
                      {ticket.notes && (
                        <p className="text-xs text-amber-700 bg-amber-50 p-2 rounded-md border border-amber-200 mt-1">
                          {ticket.notes}
                        </p>
                      )}
                    </div>

                    <div className="flex items-center justify-between sm:flex-col sm:items-end gap-2 sm:gap-4">
                      <div className="text-left sm:text-right">
                        <div className="text-xs text-muted-foreground">
                          Valor estimado
                        </div>
                        <div className="font-bold text-lg text-secondary">
                          {formatCurrency(price)}
                        </div>
                      </div>
                      <Button
                        onClick={() => setCheckoutTicketId(ticket.id)}
                        className={`w-full sm:w-auto font-bold ${isOperationMode ? 'h-14 text-base px-6' : ''}`}
                      >
                        Fazer check-out <ArrowRight className="h-4 w-4 ml-2" />
                      </Button>
                    </div>
                  </div>
                )
              })
            )}
          </div>
        </CardContent>
      </Card>

      {checkoutTicketId && (
        <CheckoutSheet
          ticketId={checkoutTicketId}
          onClose={() => setCheckoutTicketId(null)}
        />
      )}
    </>
  )
}
